import { useMemo } from 'react';
import { db } from '../instant';
import { queryAuthorizedUsersWithPermissions, UserPermission } from '../schema';

export function usePermissions() {
    const { user: authUser } = db.useAuth();
    const { data: usersData, isLoading, error } = queryAuthorizedUsersWithPermissions();

    const currentUser = useMemo(() => {
        if (!authUser?.email || !usersData?.$users) return null;
        return usersData.$users.find(u => u.email === authUser.email) || null;
    }, [authUser, usersData?.$users]);

    const isAdmin = useMemo(() => currentUser?.admin ?? false, [currentUser?.admin]);

    const hasPermission = useMemo(() => {
        return (perm: keyof UserPermission): boolean => {
            if (isAdmin) return true;
            const value = currentUser?.[perm];
            return typeof value === 'boolean' ? value : false;
        };
    }, [isAdmin, currentUser]);

    const canAccessEvent = useMemo(() => {
        return (eventId: string): boolean => {
            const allowedEventIds = currentUser?.allowedEventIds;
            if (!allowedEventIds) return false;
            if (allowedEventIds === 'all') return true;
            try {
                const ids = JSON.parse(allowedEventIds);
                return Array.isArray(ids) && ids.includes(eventId);
            } catch {
                return false;
            }
        };
    }, [currentUser?.allowedEventIds]);

    const getAllowedEventIds = useMemo(() => {
        return (): string[] | 'all' => {
            const allowed = currentUser?.allowedEventIds;
            if (!allowed) return [];
            if (allowed === 'all') return 'all';
            try {
                const ids = JSON.parse(allowed);
                return Array.isArray(ids) ? ids : [];
            } catch {
                return [];
            }
        };
    }, [currentUser?.allowedEventIds]);

    return {
        permissions: currentUser,
        isLoading,
        error,
        isAdmin,
        hasPermission,
        canAccessEvent,
        getAllowedEventIds,

        // Convenience flags for common permission checks
        canViewMap: true,
        canEditMap: hasPermission('editMapStationsNotes'),
        canViewEvents: true,
        canEditEvents: hasPermission('createEditEventInfo'),
        canViewWasteData: hasPermission('viewWasteData'),
        canEditWasteData: hasPermission('editWasteData'),
        canGenerateReports: hasPermission('generateReports'),
        canEditTodos: hasPermission('editTodos'),
        canCompleteTodos: hasPermission('completeTodos'),
        canViewInventory: hasPermission('viewInventory'),
        canManageInventory: hasPermission('manageInventory'),
        canEditUsers: hasPermission('editUsers'),
    };
}
